/** Utilidades de formato de dinero y fechas. */
import { APP_CURRENCY, type AppCurrency } from '@financial/shared';
import { useAuthStore } from '../store/auth';

const currencyFormatters: Record<AppCurrency, Intl.NumberFormat> = {
  MXN: new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
  }),
  USD: new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }),
  CAD: new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    currencyDisplay: 'code',
    minimumFractionDigits: 2,
  }),
};

const ACTIVE_CURRENCY_KEY = 'financial.active-currency';

/** Contexto de lectura para vistas agregadas; nunca convierte importes. */
export function setActiveCurrency(currency: AppCurrency): void {
  try {
    localStorage.setItem(ACTIVE_CURRENCY_KEY, currency);
  } catch {
    /* la vista sigue funcionando sin almacenamiento */
  }
}

export function currentCurrency(): AppCurrency {
  try {
    const active = localStorage.getItem(ACTIVE_CURRENCY_KEY);
    if (active && Object.hasOwn(currencyFormatters, active)) return active as AppCurrency;
  } catch {
    /* ignore */
  }
  return useAuthStore.getState().user?.defaultCurrency ?? APP_CURRENCY;
}

export function formatMoney(amount: number, currency = currentCurrency()): string {
  return currencyFormatters[currency].format(amount);
}

const dateFmt = new Intl.DateTimeFormat('es-MX', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const calendarDateFmt = new Intl.DateTimeFormat('es-MX', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

export function formatDate(iso: string): string {
  return dateFmt.format(new Date(iso));
}

/**
 * Extrae una fecha de calendario sin reinterpretarla en la zona del navegador.
 * Los vencimientos representan un día (YYYY-MM-DD), no un instante de reloj.
 */
export function calendarDateValue(iso: string): string {
  const value = /^(\d{4}-\d{2}-\d{2})/.exec(iso)?.[1];
  if (!value) throw new Error('Fecha de calendario inválida');
  const [year, month, day] = value.split('-').map(Number);
  const normalized = new Date(Date.UTC(year, month - 1, day, 12));
  if (normalized.toISOString().slice(0, 10) !== value) {
    throw new Error('Fecha de calendario inválida');
  }
  return value;
}

/** Formatea YYYY-MM-DD conservando el día aunque el valor legado esté a 00:00Z. */
export function formatCalendarDate(iso: string): string {
  return calendarDateFmt.format(new Date(`${calendarDateValue(iso)}T12:00:00.000Z`));
}

/** Instante seguro al mediodía UTC para comparar fechas de calendario. */
export function calendarDateToDate(iso: string): Date {
  return new Date(`${calendarDateValue(iso)}T12:00:00.000Z`);
}

/** Valor para input date cuando el origen ya es una fecha de calendario. */
export function toCalendarDateInputValue(iso: string): string {
  return calendarDateValue(iso);
}

/** Fecha para <input type="date"> (YYYY-MM-DD) en horario local. */
export function toDateInputValue(iso: string | Date = new Date()): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 10);
}
